/**
 * A React-wrapped YouTube player that conforms to `VideoPlayer`'s expected API.
 *
 * WARNING(kevinb): `model` is typed as `any`.
 */
import {css} from "aphrodite";
import * as React from "react";

import {
    ActionSchedulerProvider,
    type WithActionSchedulerProps,
    type WithoutActionScheduler,
} from "@khanacademy/wonder-blocks-timing";
import {encodeParams} from "@khan/data-fetch";
import Log, {Errors} from "@khan/logging";
import {InitialRequestUrl} from "@khan/shared-core";

import {VideoPlayerState} from "../constants.ts";
import {
    getPlaybackRateEnum,
    getPlaybackRateNumber,
} from "../playback-rate-helpers.ts";
import type {SharedProps} from "../types.ts";
import {
    linkTimestampsToModel,
    logYoutubeError,
    requireYouTubeAPI,
} from "../util.ts";
import {getYoutubeLanguage} from "../youtube-language.ts";

export type ErrorOptions = {
    fallbackPlayerIsTemporary?: boolean;
    forceAutoplay?: boolean;
};

type Props = SharedProps & {
    id: string;
    onError: (options?: ErrorOptions) => void;
    // TODO(riley): All of this size logic would make a high-impact removal.
    size?: {
        width: number;
        height: number;
    };
} & WithActionSchedulerProps;

type State = {
    playerReady: boolean;
};

type DefaultProps = {
    styles: Props["styles"];
};

type PlayerVars = {
    controls: string;
    enablejsapi: string;
    modestbranding: string;
    showinfo: string;
    origin: string;
    iv_load_policy: string;
    html5: string;
    fs: string;
    rel: string;
    hl: string;
    cc_lang_pref: string;
    cc_load_policy: number;
    start: number;
};

const DEFAULT_WIDTH = 800;
const DEFAULT_HEIGHT = 480;

class YouTubePlayer extends React.Component<Props, State> {
    static defaultProps: DefaultProps = {
        styles: [],
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            playerReady: false,
        };
    }

    componentDidMount() {
        // @ts-expect-error - TS2322 - Type '(() => void) | undefined' is not assignable to type '() => unknown'.
        this._cancelTriggers = requireYouTubeAPI(
            this.initializePlayer,
            this.props.onError,
        );

        // The YouTubePlayer API doesn't have an event listener that triggers
        // on mute toggle, so we have to be all creepy and just stare at it
        // every half second or so.
        this.props.schedule.interval(() => {
            this.maybeUpdateUserMuteSetting();
            // for captions, there is neither an event listener nor a way to
            // ask the player if captions are enabled, so we will just rely
            // on users setting it in their profile and eventually reloading
            // the page.
        }, 500);
    }

    componentDidUpdate(prevProps: Props) {
        const {player} = this.props.model;

        // A null muteVideos or showCaptions value means the data wasn't available yet.
        // both are true by default, so we only need to worry about turning them off
        // once the user's settings are available
        if (
            this.props.muteVideos === false &&
            prevProps.muteVideos !== false &&
            player &&
            player.isMuted &&
            player.isMuted()
        ) {
            player.unMute();
        }
        if (
            this.props.showCaptions === false &&
            prevProps.showCaptions !== false &&
            player &&
            player.unloadModule
        ) {
            player.unloadModule("captions");
        }
    }

    componentWillUnmount() {
        if (this._cancelTriggers) {
            this._cancelTriggers();
        }
    }

    _cancelTriggers: () => unknown | null | undefined = () => {};

    initializePlayer: () => void = () => {
        const {model, size, schedule, playbackRate, updatePlaybackRateSetting} =
            this.props;

        schedule.timeout(() => {
            // if we haven't gotten a ready event in 5s it probably means that
            // a redirect occurred in the iframe (ie from a firewall)
            this.fallbackIfPlayerNotReady();
        }, 5000);

        // @ts-expect-error - TS2571 - Object is of type 'unknown'. | TS2339 - Property 'YT' does not exist on type 'Window & typeof globalThis'.
        model.player = new window.YT.Player(this.props.id, {
            // TODO(charlie): Remove width and height? They seem to be
            // overridden by styles.video on the <iframe> anyway.
            width: size ? size.width : DEFAULT_WIDTH,
            height: size ? size.height : DEFAULT_HEIGHT,
            videoId: model.get("translatedYoutubeId"),
            playerVars: this.getPlayerVars(),
            events: {
                onReady: () => {
                    const {muteVideos, autoplay, onPlayerReady, onStateChange} =
                        this.props;

                    this.setState({playerReady: true});

                    // Flow: we know that the player is defined at this point.
                    const player: any = model.player;

                    if (muteVideos) {
                        player.mute();
                    }

                    player.setPlaybackRate(getPlaybackRateNumber(playbackRate));

                    // Instead of telling the API to autoplay via the player vars, we are doing this
                    // explicitly.  The reason is that sometimes the iframe loads before the API is ready.
                    // That isn't usually a problem, but if there are loading errors, the onError function
                    // won't get fired.  Instead, auto-play once the API is ready.
                    // https://khanacademy.atlassian.net/browse/LP-4121
                    if (autoplay) {
                        player.playVideo();
                    }

                    const togglePlayback = () =>
                        player.getPlayerState() === VideoPlayerState.PLAYING
                            ? player.pauseVideo()
                            : player.playVideo();

                    const initialState =
                        player.getPlayerState() || VideoPlayerState.UNSTARTED;

                    onStateChange({data: initialState});
                    onPlayerReady(togglePlayback);

                    // TODO(riley): High-impact removal (easy post-legacy).
                    model.set("playerType", "iframe");

                    model.setInitialPlayerState(
                        initialState,
                        player.getPlaybackRate(),
                    );

                    // This is not a public api, but it works and
                    // `player.getOption('captions', 'track')` doesn't return
                    // the most up to date value, it always returns the default
                    // one.
                    //
                    // Also, before the first time anything changes,
                    // `useCaptionsDetectionFallback` is still true, which is
                    // what we want, since it'll use the `getOptions` method,
                    // which does work before the captions are changed from the
                    // default.
                    //
                    // TODO(riley): HTML fallback is currently skipped, move
                    //              into controller|actions|model eventually.
                    let hasReceivedInitialCaptionEvent = false;
                    let lastKnownCaptionState: boolean | null = null;
                    player.addEventListener("captionschanged", (change) => {
                        // Ignore the first captionschanged event as it fires immediately when
                        // the event listener is added during player initialization and may not
                        // reflect the user's actual interaction with captions
                        if (!hasReceivedInitialCaptionEvent) {
                            hasReceivedInitialCaptionEvent = true;
                            return;
                        }

                        // this fires when the video starts with the initial
                        // value, so we wait until it's started to send setting
                        // updates
                        const playerState = player.getPlayerState();
                        if (
                            playerState !== VideoPlayerState.UNSTARTED &&
                            playerState !== VideoPlayerState.ENDED
                        ) {
                            // change.data contains the current captions track data.
                            // No data indicates there are currently no captions on.
                            const captionsAreCurrentlyOn = !!Object.keys(
                                change.data,
                            ).length;

                            // Only update the setting if the caption state has actually changed
                            // from what we last processed (prevents duplicate updates)
                            if (
                                lastKnownCaptionState !== captionsAreCurrentlyOn
                            ) {
                                lastKnownCaptionState = captionsAreCurrentlyOn;
                                this.maybeUpdateShowCaptionsSetting(
                                    captionsAreCurrentlyOn,
                                );
                            }
                        }

                        try {
                            const lang = change.data.languageCode;
                            if (lang != null) {
                                // `lang` might be '', which is fine.
                                model.set({
                                    captionsLocale: lang,
                                    useCaptionsDetectionFallback: false,
                                });
                            }
                        } catch (err: any) {
                            Log.error(
                                "languageCode not set for captions.",
                                Errors.Internal,
                                {cause: err},
                            );
                        }
                    });

                    // HACK: This is a result of how older parts of our code
                    //       including discussions, clarifications, and page
                    //       templates were structured. It searches the entire
                    //       DOM and adds event handlers, which does *not* mesh
                    //       well with the React model.
                    linkTimestampsToModel(player);
                },

                onStateChange: this.props.onStateChange,

                onPlaybackRateChange: (e) => {
                    // used to calculate video position data
                    model.setPlaybackRate(e.data);
                    const rate = getPlaybackRateEnum(e.data);

                    if (rate != null) {
                        updatePlaybackRateSetting(rate);
                    }
                },

                onError: ({data: errorCode}) => {
                    /**
                     * Possible errors: https://developers.google.com/youtube/iframe_api_reference#Events
                     *
                     * 2 – The request contains an invalid parameter value. For example, this error occurs if you specify a video ID that does not have 11 characters, or if the video ID contains invalid characters, such as exclamation points or asterisks.
                     * 5 – The requested content cannot be played in an HTML5 player or another error related to the HTML5 player has occurred.
                     * 100 – The video requested was not found. This error occurs when a video has been removed (for any reason) or has been marked as private.
                     * 101 – The owner of the requested video does not allow it to be played in embedded players.
                     * 150 – This error is the same as 101. It's just a 101 error in disguise!
                     */

                    if (errorCode === 5) {
                        // This is the error code for general failures in the
                        // YouTube HTML5 player. When the network provider,
                        // like a school district, allows the YouTube player
                        // to load (www.youtube.com is unblocked), but blocks
                        // the video itself (<?>.googlevideo.com is blocked),
                        // the player will trigger a generic failure, and we
                        // should switch to the fallback player.
                        //
                        // That said, this _could_ indicate a transient failure
                        // rather than a permanent failure, so we also offer
                        // the switch-to-youtube-banner UI to enable the user
                        // to switch back to the full-featured YouTube player.
                        this.switchToFallback(false, errorCode);
                    } else {
                        // For all other sorts of errors, we'll try the fallback player, but we want to signal
                        // that we want it to be temporary.
                        this.switchToFallback(true, errorCode);
                    }
                },
            },
        });
    };

    /**
     * Return an object literal of YouTube player variables. Used to set up the
     * initial iFrame and for the later call to `YT.player`.
     */
    getPlayerVars(): PlayerVars {
        const {startTime, showCaptions} = this.props;
        const subtitleLanguage = getYoutubeLanguage() || "en";
        const ccLoadPolicy = showCaptions === false ? 0 : 1;

        return {
            controls: "1",
            enablejsapi: "1",
            modestbranding: "1",
            showinfo: "0",
            origin: InitialRequestUrl.origin,
            iv_load_policy: "3", // Don't show video annotations on-site.
            html5: "1",
            fs: "1",
            rel: "0",
            hl: subtitleLanguage,
            cc_lang_pref: subtitleLanguage,
            cc_load_policy: ccLoadPolicy,
            start: startTime,
        };
    }

    youtubeIframeUrl(): string {
        const {model} = this.props;

        // We use the "youtube-nocookie" domain for, as YouTube describes it,
        // "privacy-enhanced mode". It should be a little friendlier about not
        // tracking users, especially so we can comply with US regulations about
        // tracking children online.

        const id = model.get("translatedYoutubeId");
        const params = encodeParams(this.getPlayerVars());

        return `https://www.youtube-nocookie.com/embed/${id}/?${params}`;
    }

    maybeUpdateUserMuteSetting: () => void = () => {
        const {muteVideos, updateMuteVideosSetting} = this.props;

        // Flow: we know that the player is defined at this point.
        const player: any = this.props.model.player;

        // Just in case the isMuted method isn't available yet, like when the player
        // is first initializing.
        if (!player || !player.isMuted) {
            return;
        }
        const playerIsCurrentlyMuted = player.isMuted();
        if (playerIsCurrentlyMuted === undefined) {
            return;
        }

        if (playerIsCurrentlyMuted !== muteVideos) {
            updateMuteVideosSetting(playerIsCurrentlyMuted);
        }
    };

    maybeUpdateShowCaptionsSetting: (show: boolean) => void = (show) => {
        const {showCaptions, updateShowCaptionsSetting} = this.props;

        // A null showCaptions value means the data wasn't available yet.
        if (showCaptions === null) {
            return;
        }

        updateShowCaptionsSetting(show);
    };

    fallbackIfPlayerNotReady: () => void = () => {
        if (!this.state.playerReady) {
            this.switchToFallback(true, undefined, "Player did not initialize");
        }
    };

    switchToFallback: (
        temporary: boolean,
        errorCode?: number,
        errorDescription?: string,
    ) => void = (temporary = true, errorCode, errorDescription) => {
        if (temporary) {
            this.props.onError({
                fallbackPlayerIsTemporary: true,
                forceAutoplay: true,
            });
        } else {
            this.props.onError();
        }

        logYoutubeError(
            this.props.title,
            this.youtubeIframeUrl(),
            errorCode,
            errorDescription,
        );
    };

    render(): React.ReactNode {
        const {model, title, size, styles = []} = this.props;

        const onError = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
            this.switchToFallback(
                true,
                undefined,
                `iframe received an error: ${(e as TSFixMe).message}`,
            );
        };

        return (
            <iframe
                className={
                    "player ka-player-iframe centered-when-windowed " +
                    css(...styles)
                }
                name="ka-player-iframe"
                id={this.props.id}
                title={title}
                frameBorder={0}
                onError={onError}
                allowFullScreen={true}
                src={this.youtubeIframeUrl()}
                width={size ? size.width : DEFAULT_WIDTH}
                height={size ? size.height : DEFAULT_HEIGHT}
                data-youtubeid={model.get("youtubeId")}
                data-translatedyoutubeid={model.get("translatedYoutubeId")}
                data-translatedyoutubelang={model.get("translatedYoutubeLang")}
                data-topicid={model.get("topicId")}
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- TODO: Address a11y error
                tabIndex={this.props.tabAccessible ? 0 : -1}
                allow="autoplay"
            />
        );
    }
}

type ExportProps = WithoutActionScheduler<
    JSX.LibraryManagedAttributes<
        typeof YouTubePlayer,
        React.ComponentProps<typeof YouTubePlayer>
    >
>;

export default React.forwardRef<YouTubePlayer, ExportProps>((props, ref) => {
    return (
        <ActionSchedulerProvider>
            {(schedule) => (
                <YouTubePlayer {...props} schedule={schedule} ref={ref} />
            )}
        </ActionSchedulerProvider>
    );
});
